import { AccountRoleEnum } from '../services/SessionService';

/**
 * The whole state machine (§5.3). One pure function and one enum: no DiagnosisAction,
 * no transition table, no console.log.
 *
 * develop declared all three. `applyStateTransition` (diagnosis.service.ts:165-184)
 * ignored both its `action` argument and the current state it destructured — they
 * appear only in a console.log — so the table, `getTransition` and
 * `DiagnosisStateTransition` were already unreachable, and commit 2c91386759 had
 * deleted the guard that once used them.
 *
 * Four branches of develop's version are dropped as provably unreachable given the
 * guards above them: `&& !context.isGenerating` on the admin line, both trailing
 * `&& context.isReportGenerated` clauses, and the duplicate isGenerating branch at
 * DiagnosisState.ts:69-71.
 */
export enum DiagnosisState {
  IDLE = 'IDLE',
  PROCESSING = 'PROCESSING',
  REQUIRES_ASSESSMENT = 'REQUIRES_ASSESSMENT',
  ASSESSED = 'ASSESSED',
  REVOCABLE = 'REVOCABLE',
}

/**
 * `assessmentRequired` is ENTIRELY client-computed — no API response carries such a
 * field. It means "at least one finding still needs a Yes/No answer, or has answers
 * that were not persisted", and ReportAssessmentService derives it. The name reads
 * like a server flag and is not one; a backend that did send one would be silently
 * ignored (§10 risk 1).
 *
 * `role` is nullable because sessionService.getRole() returns null after a failed
 * verifySession, and typed as the enum rather than `string` because develop compared
 * bare 'ENVIRONMENT_ADMIN' literals in two live sites (§6 defect 8).
 */
export interface DiagnosisStateContext {
  role: AccountRoleEnum | null;
  isGenerating: boolean;
  isReportGenerated: boolean;
  assessmentRequired: boolean;
}

export function getDiagnosisState(context: DiagnosisStateContext): DiagnosisState {
  if (context.isGenerating) {
    return DiagnosisState.PROCESSING;
  }

  if (!context.isReportGenerated) {
    return DiagnosisState.IDLE;
  }

  // D4: deliberately ABOVE both assessment branches, so an admin can never open the
  // assessment dialog. Confirmed with the user 2026-08-10 and asserted positively in
  // the co-located test. Also D5: this is an equality test against the enum, so a null
  // or unrecognised role falls through — the gate fails CLOSED.
  if (context.role === AccountRoleEnum.ENVIRONMENT_ADMIN) {
    return DiagnosisState.REVOCABLE;
  }

  if (context.assessmentRequired) {
    return DiagnosisState.REQUIRES_ASSESSMENT;
  }

  return DiagnosisState.ASSESSED;
}
